import { lazy } from 'react'
import LazyLoadUtils from './services/LazyloadUtils'
import Spinner from './components/Spinner'
import { WorkShiftsAction, WorkShiftsLoader } from './views/shiftList/WorkShifts.loader'
import { UpcomingShiftsAction } from './views/shiftList/UpcomingShifts.loader'
import { OpenShiftLoader } from './views/data/OpenShifts.loader'
import LoginHelpDialog from './components/LoginHelpDialog'
const ErrorPageData = lazy(() => import('./layout/ErrorPageData'))
const LoginContent = lazy(() => import('./login/LoginContent'))
const Root = lazy(() => import('./layout/Root'))
const SignOutRoute = lazy(() => import('./layout/SignOutRoute'))
const ManualInFinnish = lazy(() => import('./views/layout/ManualInFinnish'))
const ManualInEnglish = lazy(() => import('./views/layout/ManualInEnglish'))

export const pathRoot = '/'
export const pathLogin = '/login'
export const pathLoginMultifactor = '/login/multifactor'
export const pathSignOut = '/signout'
export const pathMarkShift = '/shifts/new'
export const pathCustomerPools = '/customer/pools'
export const pathOrderShift = '/shifts/order'
export const pathReportShift = '/shifts/report'
export const pathEditShift = '/shifts/edit'
export const pathPastShifts = '/shifts/done'
export const pathUpcomingShifts = '/shifts/upcoming'
export const pathShiftChanges = '/shifts/upcoming?state=changesNeedConfirmation'
export const pathNotices = '/notices'
export const pathOpenShifts = '/open-shifts'
export const pathStaffNews = '/staffnews'
export const pathCalendar = '/calendar/:year/:month/:w?'
export const pathProfile = '/profile'
export const pathChangeEmail = '/profile/email'
export const pathCreateAccount = '/register'
export const pathEnableSecondFactor = '/multiFactor'
export const pathNotifications = '/notifications'
export const pathFAQ = '/faq'
export const pathSettings = '/settings'
export const pathCustomerPrints = '/customer/prints'
export const pathWorkBenefits = '/work-benefits'
export const pathAccountMigration = '/verify'
export const pathNoAccessToMystaff = '/no-access'
export const pathOpenVacancies = '/open-vacancies'
export const pathSingleOpen = '/s'
export const pathShiftSuggestion = '/suggestion'
export const pathLandingPage = '/l'
export const pathNewOrOld = '/beta-or-old'
export const pathHowToCreateAccount = '/create-account-help'
export const pathCustomerShifts = '/customer/shifts'
export const pathCustomerEditShift = '/customer/shifts/edit'
export const pathSecondFactorError = '/multifactor/e'
export const pathFinnishManual = '/ohje'
export const pathEnglishManual = '/manual'
export const pathTrainingMaterials = '/training-materials'
export const pathUserList = '/user-list'
export const pathTspNewEmployee = '/customer/add-internal-employee'
export const pathTspResources = '/customer/internal-employees'
export const pathMyInfo = '/customer/my-info'
export const pathOfficeEdit = '/customer/my-info/office/:officeId'
export const pathCustomerContactUs = '/customer/contact-us'
export const pathAssignment = '/customer/recruit-order'
export const pathCustomerSettings = '/customer/settings'
export const pathDefaultLogin = '/signin'

/**
 * All lazy loaded paths should go through the LazyLoadUtils.retryLazyLoad method
 * to avoid chunk load errors!
 * Use this as an example: lazy: () => LazyLoadUtils.retryLazyLoad(() => import(pathToYourComponentOrScreen))
 */
const hydrateFallbackElement = <Spinner sx={{ position: 'absolute', top: '45%', left: '50%' }} />

const routes = [
  // Root route
  {
    path: pathRoot,
    lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./login/LoadingScreen')),
    errorElement: <ErrorPageData />,
    hydrateFallbackElement
  },

  // Common routes
  {
    element: <Root />,
    errorElement: <ErrorPageData />,
    hydrateFallbackElement,
    children: [
      { path: pathSignOut, element: <SignOutRoute /> },
      { path: pathLogin, element: <LoginContent /> },
      { path: pathAccountMigration, lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/accountHandling/AccountMigration')) },
      { path: pathNoAccessToMystaff, lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/accountHandling/NoMystaffAccess')) },
      { path: pathLandingPage, lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./login/LandingPage')) },
      { path: pathNewOrOld, lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./login/NewOrOldUi')) },
      { path: pathEnableSecondFactor, lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/accountHandling/EnableSecondFactor')) },
      { path: pathLoginMultifactor, lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./login/LoginMultiFactor')) },
      { path: pathSecondFactorError, lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/accountHandling/multiFactor/MfErrorEmailAndPassword')) },
      { path: pathHowToCreateAccount, element: <LoginHelpDialog /> },
      { path: pathFinnishManual, element: <ManualInFinnish /> },
      { path: pathEnglishManual, element: <ManualInEnglish /> },
      {
        // myStaff / Staffpoint routes
        path: pathRoot,
        lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./login/ProtectedRouteStaffPoint')),
        children: [
          {
            path: pathDefaultLogin,
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./login/DefaultLoginRoute'))
          },
          {
            path: pathMarkShift,
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./employee/shifts/NewShiftPage'))
          },
          {
            path: pathEditShift,
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./employee/shifts/EditShiftPage'))
          },
          {
            path: pathPastShifts,
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/shiftList/WorkShiftsData')),
            loader: WorkShiftsLoader,
            action: WorkShiftsAction,
            shouldRevalidate: () => false
          },
          { path: pathUpcomingShifts, lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/shiftList/UpcomingShiftsData')), action: UpcomingShiftsAction },
          {
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/notices/NoticesContainer')),
            children: [
              {
                path: pathNotifications + '/:skip?/:take?/:from?/:until?/:messageForm?/:onlyUnopened?/:messageType?',
                lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/data/NotificationPage'))
              },
              { path: pathNotices, lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/notices/NoticesData')) },
              { path: pathStaffNews, lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/notices/StaffNewsData')) }
            ]
          },
          {
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/data/OpenContainer')),
            children: [
              {
                path: pathOpenVacancies,
                lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/vacancyCards/VacancyCardsData')),
                shouldRevalidate: () => false,
                exact: true
              },
              {
                path: pathOpenShifts,
                children: [
                  { path: pathOpenShifts + '/:skip/:take', loader: OpenShiftLoader }
                ],
                lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/data/OpenShiftsData')),
                exact: true
              }
            ]

          },
          { path: pathOpenVacancies, lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/vacancyCards/VacancyCardsData')) },
          {
            path: pathTrainingMaterials,
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/trainingMaterials/TrainingMaterialsPage'))
          },
          {
            path: pathCalendar,
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/Calendar/CalendarData')),
            children: [
              {
                path: 'edit',
                lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./employee/shifts/EditShiftPage'))
              }
            ]

          },
          {

            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/profilePage/ProfileContainer')),
            children: [
              { path: pathProfile, lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/profilePage/ProfilePageData')) },
              { path: pathChangeEmail, lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/profilePage/ChangeEmail')) }
            ]
          },
          {
            path: pathSingleOpen,
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/data/SingleOpenScreenData'))
          },
          {
            path: pathShiftSuggestion,
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./employee/shiftSuggestion/ShiftSuggestionView'))
          },
          {
            path: pathFAQ,
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/faq/FaqPage'))
          },
          { path: pathSettings, lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/inputs/SettingsPage')) },
          { path: pathWorkBenefits, lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/workBenefits/WorkBenefitsData')) },
          /* Customer */
          { path: pathCustomerShifts, lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/customer/shifts/CustomerShiftsPage')), shouldRevalidate: () => false },
          { path: pathCustomerPools, lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./customer/CustomerPoolPage')) },
          { path: pathCustomerPrints, lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./customer/prints/CustomerPrintPage')) },
          {
            path: pathOrderShift,
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./customer/shifts/OrderShiftPage'))
          },
          {
            path: pathReportShift,
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./customer/shifts/ReportShiftPage'))
          },
          {
            path: pathCustomerEditShift,
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./customer/shifts/CustomerEditShiftPage'))
          },
          {
            path: pathTspNewEmployee,
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/tspRecources/TspAddEmployeePage'))
          },
          {
            path: pathTspResources,
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/tspRecources/TspEmployeesListPage')),
            children: [
              {
                path: 'edit/:id/customerId/:customerId',
                lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/tspRecources/TspEditInternalEmployeePage'))
              }
            ]
          },
          {
            path: pathMyInfo,
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./customer/myInfo/MyInfoPage'))
          },
          {
            path: pathOfficeEdit,
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./customer/myInfo/officeEdit/OfficeEditPage'))
          },
          {
            path: pathCustomerContactUs,
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./customer/contactUs/ContactUsPage'))
          },
          {
            path: pathAssignment,
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./customer/recruitOrder/RecruitOrderPage'))
          },
          {
            path: pathCustomerSettings,
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./customer/settings/CustomerSettingsPage'))
          }
        ]

      },
      // Staffpoint admin routes
      {
        lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./login/ProtectedRouteStaffpointAdmin')),
        children: [
          {
            path: pathUserList,
            lazy: () => LazyLoadUtils.retryLazyLoad(() => import('./views/admin/userList/UserListData'))
          }
        ]
      }
    ]
  }
]

export default routes
